import { type FC, type ReactNode } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import CheckShield from 'dibs-icons/exports/CheckShield.ts';

const getDetailsLink = (getLink: (chunk: ReactNode) => ReactNode): ReactNode =>
    getLink(<FormattedMessage id="BuyerIncentivesBanner.detailsLink" defaultMessage="Details" />);

type Props = {
    getLink?: (chunk: ReactNode) => ReactNode;
    variant?: 'default' | 'purchaseProtection';
};

export const BuyerIncentivesBanner: FC<Props> = ({ getLink, variant = 'default' }) => {
    if (variant === 'purchaseProtection') {
        return (
            <div
                className={classnames(
                    dibsCss.pSmaller,
                    dibsCss.bgShagreen50,
                    dibsCss.roundedXxsmall,
                    dibsCss.flex,
                    dibsCss.gapXsmall,
                    dibsCss.itemsStart
                )}
                data-tn="purchase-protection-banner-message-center"
            >
                <span className={dibsCss.shrink0}>
                    <CheckShield variation="24" />
                </span>
                <span className={dibsCss.sassyFontBodySmall}>
                    <FormattedMessage
                        id="BuyerIncentivesBanner.purchaseProtection.message"
                        defaultMessage="Only orders placed on 1stDibs are protected if an item is damaged, isn't authentic, or isn't as described."
                    />
                </span>
            </div>
        );
    }

    const detailsLink = getLink ? getDetailsLink(getLink) : null;

    return (
        <div
            className={classnames(
                dibsCss.pSmaller,
                dibsCss.roundedXxsmall,
                dibsCss.flex,
                dibsCss.gapXsmall,
                dibsCss.itemsStart,
                dibsCss.bgShagreen50
            )}
            data-tn="purchase-protection-banner"
        >
            <span className={dibsCss.shrink0}>
                <CheckShield variation="24" />
            </span>
            <div className={dibsCss.sassyFontBodySmall}>
                <div className={dibsCss.sassyFontBodySmallHeavy}>
                    <FormattedMessage
                        id="BuyerIncentivesBanner.incentives.protectionTitle"
                        defaultMessage="Orders on 1stDibs are protected"
                    />
                </div>
                <FormattedMessage
                    id="BuyerIncentivesBanner.incentives.descriptionWithLink"
                    defaultMessage="Buying on 1stDibs protects you if an item is damaged, not authentic, or not as described. {detailsLink}"
                    values={{
                        detailsLink,
                    }}
                />
            </div>
        </div>
    );
};
